<script setup>
import {
  defineProps,
  computed,
  reactive,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import Message from './Message.vue';
// NOTICE: KAPTION-INTEGRATION - Import UndoPendingOverlay for undo send feature
import UndoPendingOverlay from 'dashboard/components/kaption/UndoPendingOverlay.vue';
import { MESSAGE_TYPES, MESSAGE_STATUS } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useUndoSend } from 'dashboard/composables/useUndoSend.js';
import { useUISettings } from 'dashboard/composables/useUISettings';
import MessageApi from 'dashboard/api/inbox/message.js';
import KaptionPendingOutboundMessage from 'dashboard/components/kaption/KaptionPendingOutboundMessage.vue';
import * as KaptionMessageBatchesAPI from 'dashboard/api/kaptionMessageBatches';
import { emitter } from 'shared/helpers/mitt';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 * @property {Set} translatingMessages - Set of message IDs currently being translated
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
  // NOTICE: KAPTION-INTEGRATION - Pass translating messages to track which are being translated
  translatingMessages: {
    type: Set,
    default: () => new Set(),
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  const messages = useCamelCase(props.messages, { deep: true });

  return messages;
});

const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const messageBatch = ref(null);
const messageBatchBusy = ref(false);

const isGroupConversation = computed(() => currentChat.value?.group === true);

const applyBatch = response => {
  const batch = response?.data?.batch;
  messageBatch.value = batch?.status === 'open' ? batch : null;
};
const loadMessageBatch = async () => {
  if (!currentChat.value?.id || !currentAccountId.value) return;
  try {
    applyBatch(
      await KaptionMessageBatchesAPI.getMessageBatch(
        currentAccountId.value,
        currentChat.value.id
      )
    );
  } catch {
    messageBatch.value = null;
  }
};
const mutateBatch = async (method, payload) => {
  if (!messageBatch.value) return;
  messageBatchBusy.value = true;
  try {
    applyBatch(
      await method(
        currentAccountId.value,
        currentChat.value.id,
        messageBatch.value.id,
        payload
      )
    );
    emitter.emit('kaption-message-batch-updated');
  } finally {
    messageBatchBusy.value = false;
  }
};
const flushBatch = () =>
  mutateBatch(KaptionMessageBatchesAPI.flushMessageBatch);
const discardBatch = () =>
  mutateBatch(KaptionMessageBatchesAPI.discardMessageBatch);
const updateBatch = content =>
  mutateBatch(KaptionMessageBatchesAPI.updateMessageBatch, { content });

watch(() => currentChat.value?.id, loadMessageBatch, { immediate: true });
onMounted(() => emitter.on('kaption-message-batch-updated', loadMessageBatch));
onBeforeUnmount(() =>
  emitter.off('kaption-message-batch-updated', loadMessageBatch)
);

// NOTICE: KAPTION-INTEGRATION - Undo send feature setup
const { undoPendingMessages, undoMessage } = useUndoSend();
const { uiSettings } = useUISettings();

// Get configured undo duration from settings (default 15 seconds)
const undoDuration = computed(
  () => uiSettings.value?.kaption_undo_send_duration ?? 15
);

/**
 * Checks if a message is in pending_undo status
 * @param {Object} message - The message to check
 * @returns {Boolean} - Whether the message is pending undo
 */
const isPendingUndo = message => message.status === MESSAGE_STATUS.PENDING_UNDO;

/**
 * Gets the remaining seconds for a pending undo message
 * @param {Object} message - The message to get countdown for
 * @returns {Number} - Remaining seconds (defaults to configured duration)
 */
const getUndoRemainingSeconds = message => {
  const pending = undoPendingMessages.value[message.id];
  return pending?.remainingSeconds ?? undoDuration.value;
};

/**
 * Handles undo action for a pending message
 * @param {Number|String} messageId - The ID of the message to undo
 */
const handleUndo = messageId => {
  undoMessage(messageId);
};

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

// NOTICE: KAPTION-INTEGRATION - Check if the previous message has the same sender.
// Looser than shouldGroupWithNext — no time constraint, just same sender + same type.
// Used to suppress sender name on consecutive messages from the same sender (WhatsApp-style).
const hasSameSenderAsPrevious = (index, searchList) => {
  if (index === 0) return false;

  const current = searchList[index];
  const prev = searchList[index - 1];

  if (current.status === 'failed' || prev.status === 'failed') return false;

  const currentSenderId = current.senderId ?? current.sender?.id;
  const prevSenderId = prev.senderId ?? prev.sender?.id;
  if (currentSenderId !== prevSenderId) return false;

  // Must be same direction (both incoming or both outgoing)
  return current.messageType === prev.messageType;
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-4 bg-n-background">
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Wrap pending_undo messages with UndoPendingOverlay -->
      <UndoPendingOverlay
        v-if="isPendingUndo(message)"
        :remaining-seconds="getUndoRemainingSeconds(message)"
        :total-duration="undoDuration"
        @undo="handleUndo(message.id)"
      >
        <Message
          v-bind="message"
          :is-email-inbox="isAnEmailChannel"
          :is-group-conversation="isGroupConversation"
          :in-reply-to="getInReplyToMessage(message)"
          :group-with-next="shouldGroupWithNext(index, allMessages)"
          :group-with-previous="hasSameSenderAsPrevious(index, allMessages)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          :is-message-translating="props.translatingMessages.has(message.id)"
          data-clarity-mask="True"
          @retry="emit('retry', message)"
        />
      </UndoPendingOverlay>
      <!-- NOTICE: KAPTION-INTEGRATION - Regular messages render without wrapper -->
      <Message
        v-else
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :is-group-conversation="isGroupConversation"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, allMessages)"
        :group-with-previous="hasSameSenderAsPrevious(index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        :is-message-translating="props.translatingMessages.has(message.id)"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <KaptionPendingOutboundMessage
      v-if="messageBatch"
      :batch="messageBatch"
      :busy="messageBatchBusy"
      @send="flushBatch"
      @discard="discardBatch"
      @update="updateBatch"
    />
    <slot name="after" />
  </ul>
</template>
